'use client';

import { mittInstance } from '@/constants/mitt-instance';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const ServerNavigateLoading = () => {
  const [showNavigateLoading, setShowNavigateLoading] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const loadingDelayTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handler = () => {
      if (loadingDelayTimerRef.current) {
        clearTimeout(loadingDelayTimerRef.current);
      }

      loadingDelayTimerRef.current = setTimeout(() => {
        setShowNavigateLoading(true);
      }, 300);
    };

    mittInstance.on('OPEN_SERVER_NAVIGATE_LOADING', handler);

    return () => {
      mittInstance.off('OPEN_SERVER_NAVIGATE_LOADING', handler);
    };
  }, []);

  useEffect(() => {
    if (loadingDelayTimerRef.current) {
      clearTimeout(loadingDelayTimerRef.current);
      loadingDelayTimerRef.current = null;
    }

    setShowNavigateLoading(false);
  }, [pathname, searchParams]);

  useEffect(() => {
    return () => {
      if (loadingDelayTimerRef.current) {
        clearTimeout(loadingDelayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      setShowNavigateLoading(false);
    };

    window.addEventListener('popstate', handler);
    window.addEventListener('pagehide', handler);

    return () => {
      window.removeEventListener('popstate', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, []);

  return (
    <div
      className={clsx(
        'fixed left-0 top-0 z-[9999999] h-full w-full items-center justify-center bg-white/40',
        showNavigateLoading ? 'flex' : 'hidden',
      )}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='101'
        height='65'
        fill='none'
        viewBox='0 0 101 65'
        className='animate-pulse'
      >
        <g
          fill='#80583F'
          fillRule='evenodd'
          clipRule='evenodd'
          opacity='0.5'
        >
          <path d='M50.526 46.853c1.25-.118 3.05-1.059 3.951-2.088.988-1.118 1.453-2.53.174-3.5-1.017-.765-2.615-.118-3.312.559-.813.794-1.307 2.529-3.312 2.647-2.295.147-3.719-1.971-4.358-5.383-.03-.147-.058-.323-.087-.5-.32-2.03-.378-4.47-.175-7.147.059-.941.175-2.117.32-3.412.116-.97.407-2.911.784-5.794a16.5 16.5 0 0 0-3.196 1.236c-.348.206-.726.44-1.162.706l-.93.646a337 337 0 0 0-.523 4.5c-.958.089-8.658.089-11.273 0 3.429-5.294 7.787-12 14.411-20.911-.464 1.882-1.075 5.265-1.772 10.176 1.395-.647 2.935-1.176 4.678-1.647l.523-.147c.232-1.735.494-3.676.784-5.794.465-3.147.872-5.676 1.046-6.853.262-1.382.581-2.794.93-4.147-2.993 2.588-6.712 2.441-11.505 2.794-9.443.794-15.05 5.735-15.05 12.235 0 2.294.958 3.677 2.614 3.677 1.25 0 2.121-1.118 2.121-2.059 0-1.265-1.017-2.441-1.017-3.823 0-1.706 1.104-3.677 2.76-5 3.051-2.442 7.787-2.589 14.208-3.177-12.871 14.647-20.542 31.824-30.217 41.647-1.975 2-3.777 3.235-5.752 3.235-1.395 0-3.138-.676-4.068-.676C.726 48.853 0 49.794 0 50.97c0 1.764 1.453 2.735 4.155 2.735 6.479 0 12.87-8.382 21.674-22 3.458-.059 11.041 0 12.959 0v.941c.116 2.824.726 5.882 1.772 8.059 2.528 5.265 5.84 6.53 9.966 6.147'></path>
          <path d='M30.507 32.969a13 13 0 0 0-.232 2.294c0 2.294 1.017 4.5 2.76 4.5 1.25 0 1.976-1.03 1.976-2.206 0-1.235-1.424-2.47-1.627-4.559zM32.047 28.91h2.8179999999999996c1.279-2.411 3.341-4.764 5.927-6.147 3.487-1.853 7.438-2.323 7.438-2.323s-.871 2.794-1.569 6.647c-.842 4.647-1.51 9.53-1.51 12.941.29.853.697 1.765 1.103 2.265.669.794 1.22.912 1.599.912h.145c.494-.03.813-.236 1.191-.618.29-.265.64-.735.959-1.177 0-.44.029-.853.058-1.294.349-5.059.755-9.823 2.005-15.176 1.22-5.147 3.108-10.088 3.108-10.088s-1.917 1.411-4.067 1.94c-3.138.765-9.443 1.912-14.266 5.471-2.15 1.618-3.806 4.236-4.94 6.647M96.926 10.414c-1.366 0-2.47.97-2.47 2.294 0 1.47 1.54 3.088 1.54 6.412 0 4.559-2.847 9.941-7.496 17.03-5.84 8.764-11.07 14.588-14.353 18.794.058-8.295.087-17.471.697-24.942.61-7.47 1.83-13.264 1.83-13.264s-4.387 3.235-7.67 8.176c-3.748 5.588-7.409 12.559-10.576 18-3.922 6.794-7.118 11.706-7.118 11.706s-.523-4.03-.872-6.441c-.406 0-.813.03-1.19.03-1.395 0-2.645-.236-3.778-.677.407 6.176 1.133 11.794 1.889 16.53C61.857 46.237 66.68 31.943 70.893 26.62A103.4 103.4 0 0 0 69.44 41.06l.349 22.059c5.753-5.53 10.576-11.735 17.055-20.353 8.658-11.559 13.307-20.265 13.307-26.324-.029-3.794-1.191-6.029-3.225-6.029'></path>
        </g>
      </svg>
    </div>
  );
};

export default ServerNavigateLoading;
